import {
    Component,
    computed,
    effect,
    inject,
    Injector,
    input,
    OnInit,
    output,
    Signal,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { DjangoService, Routes } from '@core/services/django.service';
import { FellowTraveler } from '@generated/openapi/model/fellowTraveler';
import { CardSectionComponent } from '@shared/card-section/card-section.component';
import { CustomInputComponent } from '@shared/custom-input/custom-input.component';
import { CustomRadioComponent } from '@shared/custom-radio/custom-radio.component';
import { UpdatedTravelerData } from '@shared/incomplete-info-modal/incomplete-info-modal.component';
import { SaveButtonComponent } from '@shared/save-button/save-button.component';
import { catchError, EMPTY, startWith, tap } from 'rxjs';

type TravelerData = FellowTraveler & { position: number };

@Component({
    selector: 'app-traveler-form',
    imports: [
        ReactiveFormsModule,
        CustomInputComponent,
        CardSectionComponent,
        CustomRadioComponent,
        SaveButtonComponent,
    ],
    templateUrl: './traveler-form.component.html',
})
export class TravelerFormComponent implements OnInit {
    traveler = input.required<TravelerData>();
    // invoiceId no longer needed - new API automatically uses nearest incomplete invoice
    travelerUpdated = output<UpdatedTravelerData>(); // Emits updated traveler data
    private injector = inject(Injector);
    private djangoService = inject(DjangoService);

    isSubmitting = signal(false);
    error = signal<string | null>(null);

    travelerForm: FormGroup<{
        first_name: FormControl<string>;
        last_name: FormControl<string>;
        email: FormControl<string>;
        is_minor: FormControl<boolean>;
    }>;
    isMinorOptions = [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
    ];
    isMinorSignal!: Signal<boolean | undefined>;
    isFormComplete = signal(false);
    buttonText!: Signal<string>;

    constructor() {
        this.travelerForm = new FormGroup({
            first_name: new FormControl<string>('', [Validators.required]),
            last_name: new FormControl<string>('', [Validators.required]),
            email: new FormControl<string>(''),
            is_minor: new FormControl<boolean>(false),
        });

        this.isMinorSignal = toSignal(
            this.travelerForm.controls.is_minor.valueChanges.pipe(
                startWith(this.travelerForm.controls.is_minor.value)
            ),
            { injector: this.injector }
        );

        this.buttonText = computed(() => {
            return this.isMinorSignal() ? 'Submit' : 'Invite';
        });

        effect(() => {
            const isComplete = this.traveler().is_complete;
            this.isFormComplete.set(isComplete);
        });

        effect(() => {
            const isMinor = this.isMinorSignal();
            const emailControl = this.travelerForm.controls.email;

            if (isMinor) {
                emailControl.clearValidators();
                emailControl.setValue('');
            } else {
                emailControl.setValidators([
                    Validators.required,
                    Validators.email,
                ]);
            }
            emailControl.updateValueAndValidity();
        });
    }

    ngOnInit() {
        this.travelerForm.patchValue({
            first_name: this.traveler().first_name ?? '',
            last_name: this.traveler().last_name ?? '',
            email: this.traveler().email ?? '',
            is_minor: this.traveler().is_minor ?? false,
        });

        const isMinor = this.traveler().is_minor ?? false;
        const emailControl = this.travelerForm.controls.email;
        if (isMinor) {
            emailControl.clearValidators();
        } else {
            emailControl.setValidators([Validators.required, Validators.email]);
        }
        emailControl.updateValueAndValidity();
    }

    onSubmitTraveler() {
        if (this.isFormComplete()) return;

        if (!this.travelerForm.valid) {
            this.travelerForm.markAllAsTouched();
            return;
        }

        this.isSubmitting.set(true);
        this.error.set(null);

        const formData = this.travelerForm.value;

        const updateData: Record<string, string | boolean> = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            is_minor: formData.is_minor,
        };

        if (!formData.is_minor && formData.email) {
            updateData.email = formData.email;
        }

        this.djangoService
            .drfUpdate(
                Routes.dashboard,
                'fellow_travelers',
                updateData,
                this.traveler().id
            )
            .pipe(
                tap(() => {
                    this.isFormComplete.set(true);
                    this.isSubmitting.set(false);
                    // Emit updated traveler data to parent component
                    this.travelerUpdated.emit({
                        id: this.traveler().id,
                        ...formData,
                        email: formData.email || '',
                    } as UpdatedTravelerData);
                }),
                catchError(error => {
                    this.isSubmitting.set(false);

                    if (error.status === 400 && error.error) {
                        const errorData = error.error;

                        if (typeof errorData === 'object') {
                            let fieldErrors = false;

                            Object.keys(errorData).forEach(fieldName => {
                                const control =
                                    this.travelerForm.controls[
                                        fieldName as keyof typeof this.travelerForm.controls
                                    ];
                                if (control) {
                                    const errorMessages = Array.isArray(
                                        errorData[fieldName]
                                    )
                                        ? errorData[fieldName]
                                        : [errorData[fieldName]];
                                    control.setErrors({
                                        server: errorMessages[0],
                                    });
                                    control.markAsTouched();
                                    fieldErrors = true;
                                }
                            });

                            if (fieldErrors) {
                                return EMPTY;
                            }
                        }
                    }

                    this.error.set(
                        'Failed to update traveler information. Please try again.'
                    );
                    return EMPTY;
                })
            )
            .subscribe();
    }
}
